<script setup lang="ts">
// 对话流中的动作卡片（设计文档 §6）
//
// 规则：服务端生成 action、前端只渲染，前端不得自造类型；
// 已知类型分两种渲染形态：
//   - ask_user：一组结构化问题（单选 / 多选 / 自行输入 / 跳过），交给 AiAskCard，
//     它自己负责把用户的回答作为下一条消息发出；
//   - 其余（judge_goto / judge_parse_save）：渲染成一个按钮
//     （judge_goto 在本组件内跳转；judge_parse_save 调用父级注入的 saveHandler，
//     父级未提供或卡片缺少服务端回执时禁用并提示）。
import { computed, ref } from "vue";
import type { AiAction } from "@/apis/ai.ts";
import { GetText } from "@/text/library.ts";
import { handleGotoJudgeJob, handleGotoProblem } from "@/util/router.ts";
import AiAskCard from "@/components/ai/AiAskCard.vue";

const props = defineProps<{
  action: AiAction;
  /** 需要业务处理的动作（如 judge_parse_save）由父级注入；未提供则该动作禁用并提示 */
  saveHandler?: (action: AiAction) => Promise<void> | void;
}>();

const emit = defineEmits<{
  (e: "handled", action: AiAction): void;
}>();

/** 前端已支持的动作类型白名单（其余类型按「暂不支持」渲染，不静默丢弃） */
const KNOWN_TYPES = ["judge_goto", "judge_parse_save"];

const busy = ref(false);
/** 点击后才发现的问题（如 payload 缺少跳转目标）就地提示 */
const localHint = ref("");

/** disabled 原因：优先按错误码查文案表（服务端可能给数字码或数字字符串），取不到则显示服务端原文 */
const reasonText = computed(() => {
  const reason = props.action.reason;
  if (reason === undefined || reason === null || reason === "") {
    return "";
  }
  const code = Number(reason);
  if (Number.isInteger(code) && code > 0) {
    const [found, text] = GetText(code);
    if (found) {
      return text;
    }
  }
  return String(reason);
});

const supported = computed(() => KNOWN_TYPES.includes(props.action.type));

/** judge_parse_save 需要父级处理函数，否则视为未接入 */
const missingHandler = computed(() => props.action.type === "judge_parse_save" && !props.saveHandler);

/**
 * 保存凭证（payload.receipt）：只有生成当轮下发的卡片带它，历史消息里的卡片没有，
 * 此时保存必然失败（服务端无法验签），因此直接禁用并给出前端说明——
 * 说明优先级低于服务端 disabled/reason（见 hintText）。
 */
const receiptText = computed(() => {
  const receipt = props.action.payload?.receipt;
  return typeof receipt === "string" ? receipt.trim() : "";
});

/** judge_parse_save 缺少保存凭证：历史消息里的卡片 */
const missingReceipt = computed(() => props.action.type === "judge_parse_save" && receiptText.value === "");

const disabled = computed(
  () => props.action.disabled === true || !supported.value || missingHandler.value || missingReceipt.value,
);

const hintText = computed(() => {
  if (localHint.value) {
    return localHint.value;
  }
  // 服务端说不能用的原因优先级最高（可能是错误码，经 GetText 转文案）
  if (props.action.disabled === true) {
    return reasonText.value;
  }
  if (!supported.value) {
    return "当前版本暂不支持该操作";
  }
  if (missingHandler.value) {
    return "该操作尚未接入，暂不可用";
  }
  if (missingReceipt.value) {
    return "该动作缺少保存凭证（历史消息中的卡片无法再次保存），请重新生成解析";
  }
  return "";
});

const buttonLabel = computed(() => props.action.label || props.action.type);

/**
 * judge_goto 的跳转目标：题目只认 problem_key，提交认 judge_id。
 *
 * 为什么不再「兼容」problem_id / { type: "problem", id }：
 * 题目详情页按 /problem/{key} 查询，而 problem_id 与 payload.id 都是内部自增 id，
 * 两者是**不同的命名空间**——拿 id 拼出来的地址后端不认（点开 404 或串到别的题）。
 * 这正是「AI 教练给的题目链接是 ProblemId 而不是 Key」的同一类缺陷，
 * 前端保留这条兜底等于自己留了一条制造坏链接的通道，因此这里只接受 Key。
 *
 * 提交侧不受影响：提交详情页路由本身就是按内部 id（/judge/{judgeId}）。
 */
function resolveGotoTarget(): { judgeId: string; problemKey: string; contestId: number } {
  const payload = props.action.payload ?? {};
  const contestId = Number(payload.contest_id ?? 0) || 0;
  const type = typeof payload.type === "string" ? payload.type : "";
  const rawJudge = payload.judge_id ?? (type === "judge" ? payload.id : undefined);
  const rawProblem = payload.problem_key;
  return {
    judgeId: rawJudge === undefined || rawJudge === null ? "" : String(rawJudge),
    problemKey: rawProblem === undefined || rawProblem === null ? "" : String(rawProblem),
    contestId,
  };
}

async function handleJudgeGoto(): Promise<boolean> {
  const target = resolveGotoTarget();
  if (target.judgeId) {
    await handleGotoJudgeJob(target.judgeId, target.contestId || undefined);
    return true;
  }
  if (target.problemKey) {
    await handleGotoProblem(target.problemKey);
    return true;
  }
  return false;
}

async function handleClick() {
  if (disabled.value || busy.value) {
    return;
  }
  busy.value = true;
  localHint.value = "";
  try {
    let handled = false;
    switch (props.action.type) {
      case "judge_goto":
        handled = await handleJudgeGoto();
        if (!handled) {
          localHint.value = "动作缺少跳转目标，无法打开页面";
        }
        break;
      case "judge_parse_save":
        if (props.saveHandler) {
          await props.saveHandler(props.action);
          handled = true;
        }
        break;
      default:
        break;
    }
    if (handled) {
      emit("handled", props.action);
    }
  } catch (e) {
    console.error("ai action failed:", e);
    localHint.value = "操作失败，请稍后重试";
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <!-- 问答卡片：整组问题一起渲染（不是一排按钮），用户的回答由它自己作为消息发出 -->
  <AiAskCard
    v-if="action.type === 'ask_user'"
    :action="action"
    @handled="emit('handled', action)"
  />

  <div v-else class="ai-action-card">
    <t-button size="small" theme="primary" variant="outline" :disabled="disabled || busy" :loading="busy" @click="handleClick">
      <template v-if="action.type === 'judge_goto'" #icon><t-icon name="link" /></template>
      {{ buttonLabel }}
    </t-button>
    <!-- 禁用原因（服务端 reason 或前端的能力缺失提示） -->
    <span v-if="hintText" class="ai-action-hint">{{ hintText }}</span>
  </div>
</template>

<style scoped>
.ai-action-card {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.ai-action-hint {
  font-size: 12px;
  color: var(--td-text-color-placeholder);
  line-height: 1.5;
}
</style>
